<template>
	<BasePopover
		:id="$options.name"
		:status.sync="status"
		:target-id="targetId"
	>
		<template #title>
			<div class="title">
				<span> {{ capitalizeFirstLetter($t("Numéro de {docuemntType}", { docuemntType: $t(documentType) })) }}</span>
				<span class="float-right">{{ `${current_popover_showed}/${total_popovers_to_show}` }}</span>
			</div>
		</template>

		<template #default>
			<div class="box">
				{{ $t("Démarrer la numérotation à") }}
				<b-input
					v-model="start"
					style="width: 57px; height: 30px; display: inline-block"
				/>
			</div>

			<b-link class="link blue-color" @click="showOptions = !showOptions">
				<p v-if="!showOptions">
					{{ $t("Personnaliser mon numéro de {documentType}", { documentType: $t(documentType) }) }}
				</p>
				<p v-else>
					{{ $t("Ne pas personnaliser mon numéro de {documentType}", { documentType: $t(documentType) }) }}
				</p>
			</b-link>

			<div v-if="showOptions">
				<b-form-group class="label" :label="$t('Préfix')" label-cols="4">
					<b-form-input
						v-model="prefix"
						class="input grey-border w-100"
					/>
				</b-form-group>
				<b-form-group class="label" :label="$t('Séparateur')" label-cols="4">
					<b-form-select
						v-model="separator"
						size="sm"
						class="input grey-border w-100"
					>
						<b-form-select-option
							v-for="(itemSeparator, index) in separators"
							:key="index"
							:value="itemSeparator"
						>
							{{ itemSeparator }}
						</b-form-select-option>
					</b-form-select>
				</b-form-group>
				<b-form-group class="label" :label="$t('Longueur')" label-cols="4">
					<b-form-input
						v-model="length"
						type="number"
						class="input grey-border w-100"
					/>
				</b-form-group>
				<b-form-group class="label" :label="$t('Format')" label-cols="4">
					<b-form-select
						v-model="format"
						size="sm"
						class="input grey-border w-100"
					>
						<b-form-select-option
							v-for="({ value, text }, index) in formatValueArray"
							:key="index"
							:value="value"
						>
							{{ text }}
						</b-form-select-option>
					</b-form-select>
				</b-form-group>
			</div>

			<s-button type="" @click="onSubmit"> {{ $t("Suivant") }} </s-button>
		</template>
	</BasePopover>
</template>

<script>
	import { mapState } from "vuex";
	import { query } from "sinao-corejs";
	import i18n from "@/i18n";

	export default {
		name: "PopoverNumbering",
		props: {
			targetId: {
				type: String,
				required: true
			}
		},
		data() {
			return {
				separators: ["", "-", "/", "_", "*", "|", "#", "~"],
				formats: [
					"{prefix}{separator}{start}",
					"{prefix}{separator}{year}{separator}{start}",
					"{prefix}{separator}{year2}{separator}{start}",
					"{prefix}{separator}{year}{separator}{month}{separator}{start}",
					"{prefix}{separator}{year2}{separator}{month}{separator}{start}",
					"{prefix}{separator}{month}{separator}{start}",
					"{year}{separator}{month}{separator}{start}",
					"{year2}{separator}{month}{separator}{start}",
					"{year}{separator}{start}",
					"{year2}{separator}{start}",
					"{month}{separator}{start}",
					"{start}"
				],
				separator: "",
				format: "",
				start: 1,
				prefix: "",
				formatValue: "",
				length: 6,
				showOptions: false
			};
		},
		computed: {
			...mapState({
				appId: state => state.app.id,
				pageType: state => state.page.type,
				total_popovers_to_show: state => state.onboarding.total_popovers_to_show,
				current_popover_showed: state => state.onboarding.current_popover_showed,
				config: state => state.settings.config
			}),
			billingNumber: {
				get() {
					return this.$store.state.home.billingNumber;
				},
				set(value) {
					this.$store.commit("billingNumber", value);
				}
			},
			documentType() {
				return this.pageType === "invoices" ? "facture" : "devis";
			},
			status: {
				get() {
					return this.$store.state.onboarding.showPopoverNumbering;
				},
				set(value) {
					this.$store.commit("showPopoverNumbering", value);
				}
			},
			formatValueArray() {
				const prefix = this.prefix;
				const separator = this.separator;
				const start = this.start;
				const length = this.length;
				const newFormats = [];

				for (const format of this.formats) {
					const formatValue = this.constructBillingNumber(format, {
						prefix,
						separator,
						start,
						length
					});

					newFormats.push({ value: format, text: formatValue });
				}

				return newFormats;
			},
			computedWatchList() {
				const { prefix, separator, start, length, format } = this;

				return { prefix, separator, start, length, format };
			},
			defaultPrefix() {
				return this.config.accounting.piecenumbering[this.pageType].prefix || (this.pageType === "invoices" ? "FAC" : "DEV");
			}
		},
		watch: {
			nextDocument: {
				handler() {
					const { format, next_number, length, prefix } =
						this.$props?.nextDocument ?? {};

					this.prefix = prefix ?? this.defaultPrefix;
					this.start = next_number ?? 1;
					this.length = length ?? 6;
					this.format = this.formats[0];

					if (format) {
						const _separator = format.replace(/[a-z]/gi, "")[0];

						if (_separator) {
							this.separator = _separator;
						} else {
							this.separators[0];
						}
					}

					this.formatValue = this.constructBillingNumber(
						this.format,
						{
							prefix: this.prefix,
							separator: this.separator,
							start: this.start,
							length: this.length
						}
					);
				},
				immediate: true
			},
			computedWatchList: {
				handler() {
					this.formatValue = this.constructBillingNumber(
						this.format,
						{
							prefix: this.prefix,
							separator: this.separator,
							start: this.start,
							length: this.length
						}
					);

					this.billingNumber = this.formatValue;
				},
				immediate: false
			}
		},
		methods: {
			onSubmit() {
				if (this.length === null || this.length <= 0) {
					return this.$bvToast.toast(
						i18n.t('La longueur doit être supérieur ou égal à 1.'),
						{
							title: i18n.t('Erreur d’enregistrement'),
							variant: "danger",
							solid: true
						}
					);
				}

				// Format the format
				let apiFormat = this.format.replace(/{prefix}/g, "");

				apiFormat = apiFormat.replace(/{separator}/g, this.separator);
				apiFormat = apiFormat.replace(/{year}/g, "Y");
				apiFormat = apiFormat.replace(/{year2}/g, "y");
				apiFormat = apiFormat.replace(/{month}/g, "m");
				apiFormat = apiFormat.replace(/{start}/g, "x");

				const settingKey = `accounting.piecenumbering.${this.pageType}`;
				const settings = {};

				if (!this.lastDocument) {
					settings[`${settingKey}.start`] = this.start - 1;
				}

				settings[`${settingKey}.format`] = apiFormat;
				settings[`${settingKey}.length`] = this.length;
				settings[`${settingKey}.prefix`] = this.format.includes(
					"prefix"
				)
					? this.prefix
					: "";

				// Onboarding status
				settings["installation.onboarding.showPopoverNumbering"] = 1;
        settings["installation.onboarding-app.invoice_number_format"] = {format: this.format, prefix: this.prefix, separator: this.separator, length: this.length, start: this.start},


        query({
					operationId: "app.settings.update",
					params: {
						appId: this.appId
					},
					data: { settings }
				});

				// this.$emit("input", this.formatValue);
				this.billingNumber = this.formatValue;

				this.next();
			},
			next() {
				query({
					operationId: "app.settings.update",
					params: {
						appId: this.appId
					},
					data: {
						settings: {
							"installation.onboarding.showPopoverNumbering": 1,
						}
					}
				});

				this.status = false;
        this.$store.commit("setPopovers", { key: "showPopoverNumbering", value: false });
        this.$nextTick(() => {
          this.$store.dispatch('setNextOpenedPopover');
          this.$store.dispatch('incrementCurrentPopoverShowed');
        });
			},
			capitalizeFirstLetter(string) {
				if (!string) return '';
				return string.charAt(0).toUpperCase() + string.slice(1);
			}
		}
	};
</script>

<style lang="scss"></style>
